<template>
  <div v-if="!makingApiCall" class="reaction-options">
    <visual-input
      v-for="likeEmote in likeEmotes"
      :id="likeEmote.reactTypeId.toString()"
      :key="likeEmote.reactTypeId"
      v-model="selectedReact"
      name="react_type_id"
      :value="likeEmote.reactTypeId.toString()"
      type="radio"
      :aria-label="likeEmote.reactTypeEmoji"
      data-vv-as="react type"
      @change="handleSaveLikeReact"
    >
      {{/* TODO: CI - INTELLIGENT STYLING BASED ON WHICH REACT USER HAS CHOSEN */ }}
      <span class="btn -material -ico -plain">
        {{ likeEmote.reactTypeEmoji }}
      </span>
    </visual-input>
  </div>
</template>

<script>
// @flow
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { fbAPIPromise } from '@/utils/http-api'
import VisualInput from '@/components/forms/visual-input.vue'

export default {
  name: 'LikeReactForm',
  components: { VisualInput },
  props: {
    liked: {
      type: Object,
      default: null,
    },
    likeableId: {
      type: Number,
      required: true,
    },
    likeableType: {
      type: String,
      required: true,
    },
    likeEmotes: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedReact: this.liked?.reactTypeId ? this.liked.reactTypeId.toString() : '',
      makingApiCall: false,
    }
  },

  computed: {
    ...mapState(useUserAuthStore, ['user']),
  },

  methods: {
    ...mapActions(useModalsStore, ['showLoginModal']),

    handleSaveLikeReact() {
      if (!this.user.isLoggedIn) {
        this.showLoginModal()
        return
      }

      this.$emit('close-react-form')

      if (this.makingApiCall) {
        return
      }

      // no current Like React for user on Likeable Id + Type, create new one
      if (this.liked === null) {
        this.addLike()
        return
      }

      // previously selected react was selected again, remove the like react
      if (this.liked.reactTypeId.toString() === this.selectedReact) {
        this.removeLike()
        return
      }

      // previously selected like react differs from currently selected, update the like's
      // react to the currently selected react
      if (this.liked.reactTypeId.toString() !== this.selectedReact) {
        this.updateLike()
        return
      }
    },

    async addLike() {
      this.makingApiCall = true

      this.$emit('like-incremented', this.selectedReact)

      try {
        const res = await fbAPIPromise({
          callType: 'post',
          url: '/like',
          body: {
            likeable_type: this.likeableType,
            likeable_id: this.likeableId,
            react_type_id: this.selectedReact,
          },
        })

        if (!res || !res.body || !res.body.data) {
          throw new Error(`Unable to Add Like, please try again`)
        }

        this.$emit('updated-like', res.body.data)
      } catch (err) {
        this.$emit('like-decremented', this.selectedReact)
        this.$emit('updated-like', null)
      } finally {
        this.makingApiCall = false
      }
    },

    async removeLike() {
      this.makingApiCall = true

      this.$emit('like-decremented', this.selectedReact)

      try {
        await fbAPIPromise({
          callType: 'delete',
          url: `/like/${this.liked.id}`,
          body: {
            likeable_type: this.likeableType,
            likeable_id: this.likeableId,
            react_type_id: this.selectedReact,
          },
        })

        this.$emit('updated-like', null)
      } catch (err) {
        this.$emit('like-incremented', this.selectedReact)
        this.$emit('updated-like', this.liked)
      } finally {
        this.makingApiCall = false
      }
    },

    async updateLike() {
      this.makingApiCall = true

      this.$emit('like-decremented', this.liked.reactTypeId)
      this.$emit('like-incremented', this.selectedReact)

      try {
        const res = await fbAPIPromise({
          callType: 'put',
          url: `/like/${this.liked.id}`,
          body: {
            likeable_type: this.likeableType,
            likeable_id: this.likeableId,
            react_type_id: this.selectedReact,
          },
        })

        if (!res || !res.body || !res.body.data) {
          throw new Error(`Unable to Update Like, please try again`)
        }

        this.$emit('updated-like', res.body.data)
      } catch (err) {
        this.$emit('like-decremented', this.selectedReact)
        this.$emit('like-incremented', this.liked.reactTypeId)
        this.$emit('updated-like', this.liked)
      } finally {
        this.makingApiCall = false
      }
    },
  },
}
</script>
